import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaClone } from '@react-icons/all-files/fa/FaClone';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import ProfaseModal from '../../commons/ProfaseModal';
import Confirm, { useConfirm } from '../../commons/Confirm';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import { profaseDataGridSx } from '../../commons/profaseDataGridTheme';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  anularProcesosInstancia,
  archivarInstancia,
  generarProcesosInstancia,
  getEntities,
  patchGenerarAutomaticamente,
  revertirArchivadoInstancia,
} from '../../manager/profase-periodo-actividad-instancia.reducer';
import {
  buildInstanciaTipoResumenParams,
  INSTANCIA_ESTADO_VISTA_LABEL,
  INSTANCIA_ESTADO_VISTA_RESUMEN,
  parseInstanciaEstadoVista,
  type InstanciaEstadoVista,
} from '../../shared/periodo-actividad-instancia-filters';
import PeriodoTipoResumenBar, { type PeriodoTipoResumen } from '../../shared/periodo-tipo-resumen-bar';
import { formatTipoPeriodoLabel } from '../../shared/workflow-periodicidad';

export type { InstanciaEstadoVista };
export { parseInstanciaEstadoVista };

const ITEMS_PER_PAGE = 15;

const ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  PENDIENTE: { label: 'Pendiente', bg: '#fef9c3', color: '#854d0e' },
  NUEVO: { label: 'Nuevo', bg: '#ecfdf5', color: '#047857' },
  GENERADO: { label: 'Generado', bg: '#eff6ff', color: '#1d4ed8' },
  ARCHIVADO: { label: 'Archivado', bg: '#f1f5f9', color: '#475569' },
};

export interface ProfasePeriodoActividadInstanciaRow {
  id: number;
  estado?: string;
  generarAutomaticamente?: boolean | null;
  periodoActividad?: {
    id?: number;
    nombre?: string;
    tipoPeriodo?: string;
    numeroPeriodo?: number | null;
  };
  periodo?: {
    id: number;
    descripcion?: string;
    tipoLabel?: string;
    estado?: string;
    tipo?: string;
    anio?: number;
    numero?: number;
  };
}

function axiosErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; message?: string } | undefined;
    return data?.detail ?? data?.message ?? fallback;
  }
  return fallback;
}

function TipoLabel({ tipo }: { tipo?: string | null }) {
  if (!tipo) return <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
  return <span>{formatTipoPeriodoLabel(tipo)}</span>;
}

function EstadoBadge({ estado }: { estado?: string }) {
  if (!estado) return <span style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>—</span>;
  const cfg = ESTADO_CONFIG[estado.toUpperCase()] ?? { label: estado, bg: '#f1f5f9', color: '#475569' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 6px',
        height: 18,
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        background: cfg.bg,
        color: cfg.color,
        letterSpacing: '0.03em',
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
}

function instanciaSelectLabel(row: ProfasePeriodoActividadInstanciaRow): string {
  const act = row.periodoActividad?.nombre?.trim();
  const per = row.periodo?.descripcion?.trim();
  if (act && per) return `${act} · ${per}`;
  return per ?? act ?? `#${row.id}`;
}

function puedeGenerarProcesos(estado: string): boolean {
  return estado === 'GENERADO' || estado === 'NUEVO';
}

function puedeAnularProcesos(estado: string): boolean {
  return estado === 'GENERADO' || estado === 'NUEVO' || estado === 'ARCHIVADO';
}

interface GenerarProcesosResponse {
  cantidad?: number;
  cantidadOmitidas?: number;
}

interface AnularProcesosResponse {
  cantidad?: number;
  cantidadOmitidas?: number;
}

function InstanciaEstadoVistaToggle({
  value,
  onChange,
}: {
  value: InstanciaEstadoVista;
  onChange: (vista: InstanciaEstadoVista) => void;
}) {
  return (
    <div className="actividades-vista-toggle" role="group" aria-label="Filtro por estado de instancia">
      <div className="actividades-vista-toggle__options">
        {INSTANCIA_ESTADO_VISTA_RESUMEN.map(vista => (
          <button
            key={vista}
            type="button"
            className={`actividades-vista-toggle__btn${value === vista ? ' is-active' : ''}`}
            aria-pressed={value === vista}
            onClick={() => onChange(vista)}
          >
            {INSTANCIA_ESTADO_VISTA_LABEL[vista]}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface ProcesosPeriodoInstanciaViewProps {
  active?: boolean;
  estadoVista: InstanciaEstadoVista;
  tipoPeriodo?: string | null;
  listSearch?: string;
  onEstadoVistaChange: (vista: InstanciaEstadoVista) => void;
  onTipoPeriodoFilter?: (tipoPeriodo: string) => void;
  onSelectInstancia: (instancia: ProfasePeriodoActividadInstanciaRow) => void;
  onArchivadoChange?: () => void;
  onBack?: () => void;
  backLabel?: string;
}

export function ProcesosPeriodoInstanciaView({
  active = true,
  estadoVista,
  tipoPeriodo,
  listSearch = '',
  onEstadoVistaChange,
  onTipoPeriodoFilter,
  onSelectInstancia,
  onArchivadoChange,
  onBack,
  backLabel = 'Cerrar',
}: ProcesosPeriodoInstanciaViewProps) {
  const dispatch = useAppDispatch();
  const entities = useAppSelector(
    s => s.profasePeriodoActividadInstancia.entities,
  ) as ProfasePeriodoActividadInstanciaRow[];
  const totalItems = useAppSelector(s => s.profasePeriodoActividadInstancia.totalItems);
  const loading = useAppSelector(s => s.profasePeriodoActividadInstancia.loading);
  const updating = useAppSelector(s => s.profasePeriodoActividadInstancia.updating);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: ITEMS_PER_PAGE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'desc' },
  ]);
  const [actionInstanciaId, setActionInstanciaId] = useState<number | null>(null);
  const [tipoResumen, setTipoResumen] = useState<PeriodoTipoResumen[]>([]);
  const [resumenLoading, setResumenLoading] = useState(false);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const fetchTipoResumen = useCallback(async () => {
    if (!active) return;
    setResumenLoading(true);
    try {
      const params = buildInstanciaTipoResumenParams(listSearch);
      params.append('cacheBuster', String(Date.now()));
      const res = await axios.get<PeriodoTipoResumen[]>(
        `api/profase-periodo-actividad-instancias/tipo/resumen?${params.toString()}`,
      );
      setTipoResumen(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTipoResumen([]);
    } finally {
      setResumenLoading(false);
    }
  }, [active, listSearch]);

  const fetchInstancias = useCallback(() => {
    const sortItem = sortModel[0] ?? { field: 'id', sort: 'desc' as const };
    const filters: { filterName: string; filterValue: string }[] = [
      { filterName: 'estado', filterValue: estadoVista },
    ];
    if (tipoPeriodo?.trim()) {
      filters.push({ filterName: 'tipoPeriodo', filterValue: tipoPeriodo.trim() });
    }

    dispatch(
      getEntities({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        sort: `${sortItem.field},${sortItem.sort}`,
        filters,
      }),
    );
  }, [dispatch, paginationModel, sortModel, estadoVista, tipoPeriodo]);

  useEffect(() => {
    if (!active) return;
    fetchInstancias();
  }, [active, fetchInstancias]);

  useEffect(() => {
    if (!active) return;
    fetchTipoResumen();
  }, [active, fetchTipoResumen]);

  useEffect(() => {
    if (!active) return;
    setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [active, estadoVista, tipoPeriodo]);

  useEffect(() => {
    if (active) return;
    setPaginationModel({ page: 0, pageSize: ITEMS_PER_PAGE });
    setSortModel([{ field: 'id', sort: 'desc' }]);
    setActionInstanciaId(null);
    setTipoResumen([]);
  }, [active]);

  useEffect(() => {
    setConfirmLoading(updating);
  }, [updating, setConfirmLoading]);

  const rows = entities ?? [];

  const refreshAfterMutation = useCallback(() => {
    fetchInstancias();
    fetchTipoResumen();
    onArchivadoChange?.();
  }, [fetchInstancias, fetchTipoResumen, onArchivadoChange]);

  const runArchivar = async (instancia: ProfasePeriodoActividadInstanciaRow) => {
    setActionInstanciaId(instancia.id);
    try {
      await dispatch(archivarInstancia(instancia.id)).unwrap();
      showToast('success', `Instancia "${instanciaSelectLabel(instancia)}" archivada.`);
      refreshAfterMutation();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudo archivar la instancia.'));
    } finally {
      setActionInstanciaId(null);
      dismiss();
    }
  };

  const handleArchivarClick = (instancia: ProfasePeriodoActividadInstanciaRow) => {
    ask(
      <>
        ¿Archivar la instancia <strong>{instanciaSelectLabel(instancia)}</strong>?
        {' '}Los workflows asociados en proceso o finalizados también se archivarán.
      </>,
      () => { void runArchivar(instancia); },
      { title: 'Archivar instancia', confirmLabel: 'Archivar', variant: 'warning' },
    );
  };

  const handleRevertirClick = async (instancia: ProfasePeriodoActividadInstanciaRow) => {
    setActionInstanciaId(instancia.id);
    try {
      await dispatch(revertirArchivadoInstancia(instancia.id)).unwrap();
      showToast('success', `Archivado revertido para "${instanciaSelectLabel(instancia)}".`);
      refreshAfterMutation();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudo revertir el archivado.'));
    } finally {
      setActionInstanciaId(null);
    }
  };

  const runGenerarProcesos = async (instancia: ProfasePeriodoActividadInstanciaRow) => {
    setActionInstanciaId(instancia.id);
    try {
      const res = await dispatch(generarProcesosInstancia(instancia.id)).unwrap();
      const data = res.data as GenerarProcesosResponse;
      const creados = Number(data?.cantidad) || 0;
      const omitidas = Number(data?.cantidadOmitidas) || 0;
      const omitidasMsg = omitidas > 0 ? ` (${omitidas} omitidos)` : '';
      showToast('success', `Se crearon ${creados} proceso${creados === 1 ? '' : 's'} para "${instanciaSelectLabel(instancia)}"${omitidasMsg}.`);
      refreshAfterMutation();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudieron crear los procesos.'));
    } finally {
      setActionInstanciaId(null);
      dismiss();
    }
  };

  const handleCrearProcesosClick = (instancia: ProfasePeriodoActividadInstanciaRow) => {
    ask(
      <>
        ¿Crear procesos de workflow para <strong>{instanciaSelectLabel(instancia)}</strong>?
        {' '}Se generará una instancia por cada empresa del catálogo (omite duplicados).
      </>,
      () => { void runGenerarProcesos(instancia); },
      { title: 'Crear procesos', confirmLabel: 'Crear procesos', variant: 'info' },
    );
  };

  const runAnularProcesos = async (instancia: ProfasePeriodoActividadInstanciaRow) => {
    setActionInstanciaId(instancia.id);
    try {
      const res = await dispatch(anularProcesosInstancia(instancia.id)).unwrap();
      const data = res.data as AnularProcesosResponse;
      const eliminados = Number(data?.cantidad) || 0;
      const conservados = Number(data?.cantidadOmitidas) || 0;
      const conservadosMsg = conservados > 0 ? ` (${conservados} finalizados conservados)` : '';
      showToast('success', `Se anularon ${eliminados} proceso${eliminados === 1 ? '' : 's'} para "${instanciaSelectLabel(instancia)}"${conservadosMsg}.`);
      refreshAfterMutation();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudieron anular los procesos.'));
    } finally {
      setActionInstanciaId(null);
      dismiss();
    }
  };

  const handleAnularProcesosClick = (instancia: ProfasePeriodoActividadInstanciaRow) => {
    ask(
      <>
        ¿Anular los procesos no finalizados de <strong>{instanciaSelectLabel(instancia)}</strong>?
        {' '}Se eliminan los que no terminaron; los finalizados se conservan.
      </>,
      () => { void runAnularProcesos(instancia); },
      { title: 'Anular procesos', confirmLabel: 'Anular', variant: 'danger' },
    );
  };

  const handleToggleAutomatico = async (instancia: ProfasePeriodoActividadInstanciaRow, generarAutomaticamente: boolean) => {
    setActionInstanciaId(instancia.id);
    try {
      await dispatch(patchGenerarAutomaticamente({ id: instancia.id, generarAutomaticamente })).unwrap();
      showToast(
        'success',
        generarAutomaticamente
          ? `Generación automática de procesos activada para "${instanciaSelectLabel(instancia)}".`
          : `Generación automática de procesos desactivada para "${instanciaSelectLabel(instancia)}".`,
      );
      refreshAfterMutation();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudo actualizar la generación automática.'));
    } finally {
      setActionInstanciaId(null);
    }
  };

  const cols: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', flex: 0.4, minWidth: 56 },
      {
        field: 'estado',
        headerName: 'Estado',
        flex: 0.5,
        minWidth: 90,
        sortable: false,
        renderCell: params => (
          <div className="procesos-periodos-estado-cell">
            <EstadoBadge estado={(params.row as ProfasePeriodoActividadInstanciaRow).estado} />
          </div>
        ),
      },
      {
        field: 'periodoActividad.nombre',
        headerName: 'Descripción',
        flex: 1,
        minWidth: 130,
        renderCell: params => {
          const nombre = (params.row as ProfasePeriodoActividadInstanciaRow).periodoActividad?.nombre;
          return nombre ?? <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
        },
      },
      {
        field: 'periodo.descripcion',
        headerName: 'Periodo',
        flex: 0.9,
        minWidth: 120,
        renderCell: params => {
          const row = params.row as ProfasePeriodoActividadInstanciaRow;
          const label = row.periodo?.descripcion?.trim();
          if (!label) return <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-start"
              style={{ fontSize: 13, textDecoration: 'none' }}
              onClick={() => onSelectInstancia(row)}
            >
              {label}
            </button>
          );
        },
      },
      {
        field: 'periodo.tipo',
        headerName: 'Tipo periodo',
        flex: 0.5,
        minWidth: 88,
        renderCell: params => {
          const periodo = (params.row as ProfasePeriodoActividadInstanciaRow).periodo;
          return <TipoLabel tipo={periodo?.tipoLabel ?? periodo?.tipo} />;
        },
      },
      {
        field: 'generarAutomaticamente',
        headerName: 'Auto',
        flex: 0.45,
        minWidth: 72,
        sortable: false,
        renderCell: params => {
          const row = params.row as ProfasePeriodoActividadInstanciaRow;
          const busy = updating && actionInstanciaId === row.id;
          return (
            <input
              type="checkbox"
              checked={row.generarAutomaticamente === true}
              disabled={busy}
              title="Generar procesos automáticamente"
              aria-label={`Generar procesos automáticamente para ${instanciaSelectLabel(row)}`}
              onChange={e => {
                e.stopPropagation();
                void handleToggleAutomatico(row, e.target.checked);
              }}
            />
          );
        },
      },
      {
        field: '__actions',
        headerName: '',
        flex: 1.4,
        minWidth: 400,
        sortable: false,
        renderCell: params => {
          const row = params.row as ProfasePeriodoActividadInstanciaRow;
          const estado = String(row.estado ?? '').toUpperCase();
          const busy = updating && actionInstanciaId === row.id;

          return (
            <div className="procesos-periodos-actions-cell">
              {puedeGenerarProcesos(estado) && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={busy}
                  onClick={() => handleCrearProcesosClick(row)}
                >
                  Crear procesos
                </button>
              )}
              {puedeAnularProcesos(estado) && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  disabled={busy}
                  onClick={() => handleAnularProcesosClick(row)}
                >
                  Anular
                </button>
              )}
              {estado === 'GENERADO' && (
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm"
                  disabled={busy}
                  onClick={() => handleArchivarClick(row)}
                >
                  Archivar
                </button>
              )}
              {estado === 'ARCHIVADO' && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={busy}
                  onClick={() => { void handleRevertirClick(row); }}
                >
                  Revertir
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [onSelectInstancia, updating, actionInstanciaId, handleArchivarClick, handleCrearProcesosClick, handleAnularProcesosClick, handleRevertirClick],
  );

  return (
    <>
      {onBack ? (
        <div style={s.header}>
          <h2 className="profase-modal-title m-0">
            <FaClone style={{ marginRight: 8, verticalAlign: '-2px' }} />
            Instancias de periodo actividad
          </h2>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onBack}>
            {backLabel}
          </button>
        </div>
      ) : null}

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <p style={s.hint}>
        Instancias que vinculan catálogos de actividad con periodos fiscales. Crear procesos genera workflows por empresa;
        Anular elimina los no finalizados. Archivar aplica a la instancia y sus workflows; el periodo fiscal no cambia.
      </p>

      <PeriodoTipoResumenBar
        rows={tipoResumen}
        loading={resumenLoading}
        onTipoClick={onTipoPeriodoFilter ?? (() => {})}
        subtitle="Por tipo de catálogo"
        filterHint="tipo catálogo"
      />

      <div style={s.filters}>
        <InstanciaEstadoVistaToggle value={estadoVista} onChange={onEstadoVistaChange} />
      </div>

      <DataGrid
        rows={rows}
        columns={cols}
        getRowId={row => row.id}
        autoHeight
        pagination
        paginationMode="server"
        sortingMode="server"
        disableRowSelectionOnClick
        disableColumnMenu
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={model => {
          setSortModel(model.length > 0 ? model : [{ field: 'id', sort: 'desc' }]);
        }}
        rowCount={totalItems ?? rows.length}
        loading={loading}
        pageSizeOptions={[ITEMS_PER_PAGE]}
        localeText={{ noRowsLabel: 'Sin instancias de periodo actividad' }}
        sx={{
          ...profaseDataGridSx,
          '& .MuiDataGrid-cell, & .MuiDataGrid-cellContent': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-cellContent': {
            width: '100%',
          },
          '& .procesos-periodos-estado-cell, & .procesos-periodos-actions-cell': {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            lineHeight: 1,
          },
          '& .procesos-periodos-actions-cell': {
            gap: 6,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
          },
        }}
      />
    </>
  );
}

interface ProcesosPeriodoInstanciaModalProps {
  open: boolean;
  estadoVista: InstanciaEstadoVista;
  tipoPeriodo?: string | null;
  listSearch?: string;
  onEstadoVistaChange: (vista: InstanciaEstadoVista) => void;
  onTipoPeriodoFilter?: (tipoPeriodo: string) => void;
  onClose: () => void;
  onSelectInstancia: (instancia: ProfasePeriodoActividadInstanciaRow) => void;
  onArchivadoChange?: () => void;
}

export default function ProcesosPeriodoInstanciaModal({
  open,
  estadoVista,
  tipoPeriodo,
  listSearch,
  onEstadoVistaChange,
  onTipoPeriodoFilter,
  onClose,
  onSelectInstancia,
  onArchivadoChange,
}: ProcesosPeriodoInstanciaModalProps) {
  return (
    <ProfaseModal
      open={open}
      titleId="procesos-periodo-actividad-instancia-modal-title"
      size="xlarge"
      scroll
    >
      <div style={s.header}>
        <h2 id="procesos-periodo-actividad-instancia-modal-title" className="profase-modal-title m-0">
          <FaClone style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Instancias de periodo actividad
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>

      <ProcesosPeriodoInstanciaView
        active={open}
        estadoVista={estadoVista}
        tipoPeriodo={tipoPeriodo}
        listSearch={listSearch}
        onEstadoVistaChange={onEstadoVistaChange}
        onTipoPeriodoFilter={onTipoPeriodoFilter}
        onSelectInstancia={onSelectInstancia}
        onArchivadoChange={onArchivadoChange}
      />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  hint: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
};
